// Common constants used throughout the SAS Frontend application

// ==================== COLORS ====================
export const COLORS = {
  // Primary theme colors
  PRIMARY: {
    MAIN: '#2563eb',           // Main theme blue used throughout
    LIGHT: '#2563eb/10',       // Light version with opacity
    HOVER: '#2563eb/90',       // Hover state with opacity
    BLUE_600: '#2563eb',       // Tailwind blue-600
    BLUE_700: '#1d4ed8',       // Tailwind blue-700
    BLUE_500: '#3b82f6',       // Tailwind blue-500
    BLUE_100: '#dbeafe',       // Tailwind blue-100
    BLUE_900: '#1e3a8a',       // Tailwind blue-900
  },
  
  // Gray scale colors
  GRAY: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  
  // Status colors
  STATUS: {
    ONLINE: '#10b981',         // emerald-500
    OFFLINE: '#6b7280',        // gray-500
    BUSY: '#ef4444',           // red-500
    AWAY: '#f59e0b',           // amber-500
  },
  
  // Semantic colors
  SEMANTIC: {
    SUCCESS: '#10b981',        // emerald-500
    ERROR: '#ef4444',          // red-500
    WARNING: '#f59e0b',        // amber-500
    INFO: '#3b82f6',           // blue-500
  },
  
  // Red color scale
  RED: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
  
  // White and transparent
  WHITE: '#ffffff',
  TRANSPARENT: 'transparent',
};

// ==================== CSS CLASSES ====================
export const CSS_CLASSES = {
  // Button styles
  BUTTON: {
    PRIMARY: 'bg-[#2563eb] hover:bg-[#2563eb]/90 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    SECONDARY: 'bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-300 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
    BLUE: 'bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
    DISABLED: 'bg-gray-400 cursor-not-allowed opacity-50',
  },
  
  // Input styles
  INPUT: {
    FIELD: 'w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200',
    WITH_ICON: 'w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors',
  },
  
  // Card styles
  CARD: {
    DEFAULT: 'bg-white rounded-xl shadow-sm border border-gray-200 p-6',
    STAT: 'rounded-xl border border-gray-200 bg-white p-5 shadow-sm',
    COMPACT: 'bg-white rounded-lg shadow-sm border border-gray-200 p-4',
  },
  
  // Status pills
  STATUS_PILL: {
    BASE: 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium text-gray-700',
    ONLINE: 'bg-emerald-100',
    OFFLINE: 'bg-gray-100',
    BUSY: 'bg-red-100',
    AWAY: 'bg-amber-100',
  },
  
  // Icon containers
  ICON_CONTAINER: {
    SMALL: 'flex h-8 w-8 items-center justify-center rounded-full',
    MEDIUM: 'flex h-10 w-10 items-center justify-center rounded-md',
    LARGE: 'flex h-12 w-12 items-center justify-center rounded-lg',
  },
  
  // Tab styles
  TAB: {
    BASE: 'rounded-md px-2 py-1 text-xs font-medium transition-colors whitespace-nowrap min-w-0',
    ACTIVE: 'bg-[#2563eb] text-white',
    INACTIVE: 'text-gray-600 hover:bg-gray-100',
  },
  
  // Chat bubble styles
  CHAT_BUBBLE: {
    ME: 'bg-[#e6eef6] text-gray-800',
    OTHER: 'bg-gray-100 text-gray-800',
    TIME_ME: 'text-gray-600',
    TIME_OTHER: 'text-gray-500',
  },
};

// ==================== STATUS VALUES ====================
export const STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  BUSY: 'busy',
  AWAY: 'away',
};

// ==================== COMMON SYMBOLS ====================
export const SYMBOLS = {
  STAR: '★',
  ARROW_UP: '↑',
  ARROW_DOWN: '↓',
  CHECK: '✓',
  CROSS: '✗',
  BULLET: '•',
};

// ==================== ANIMATION CLASSES ====================
export const ANIMATIONS = {
  FADE_IN: 'animate-fade-in',
  SLIDE_UP: 'animate-slide-up',
  SPIN: 'animate-spin',
  PULSE: 'animate-pulse',
  BOUNCE: 'animate-bounce',
};

// ==================== TRANSITION CLASSES ====================
export const TRANSITIONS = {
  COLORS: 'transition-colors duration-200',
  ALL: 'transition-all duration-200',
  OPACITY: 'transition-opacity duration-200',
  TRANSFORM: 'transition-transform duration-200',
};

// ==================== SPACING CLASSES ====================
export const SPACING = {
  PADDING: {
    SMALL: 'p-2',
    MEDIUM: 'p-4',
    LARGE: 'p-6',
    XL: 'p-8',
  },
  MARGIN: {
    SMALL: 'm-2',
    MEDIUM: 'm-4',
    LARGE: 'm-6',
    XL: 'm-8',
  },
  GAP: {
    SMALL: 'gap-2',
    MEDIUM: 'gap-4',
    LARGE: 'gap-6',
    XL: 'gap-8',
  },
};

// ==================== BORDER RADIUS ====================
export const BORDER_RADIUS = {
  SMALL: 'rounded',
  MEDIUM: 'rounded-lg',
  LARGE: 'rounded-xl',
  FULL: 'rounded-full',
};

// ==================== SHADOW CLASSES ====================
export const SHADOWS = {
  SMALL: 'shadow-sm',
  MEDIUM: 'shadow',
  LARGE: 'shadow-lg',
  XL: 'shadow-xl',
};

// ==================== FONT WEIGHTS ====================
export const FONT_WEIGHTS = {
  NORMAL: 'font-normal',
  MEDIUM: 'font-medium',
  SEMIBOLD: 'font-semibold',
  BOLD: 'font-bold',
};

// ==================== FONT SIZES ====================
export const FONT_SIZES = {
  XS: 'text-xs',
  SM: 'text-sm',
  BASE: 'text-base',
  LG: 'text-lg',
  XL: 'text-xl',
  '2XL': 'text-2xl',
  '3XL': 'text-3xl',
};

// ==================== COMMON COMBINATIONS ====================
export const COMMON_COMBINATIONS = {
  // Primary button with icon container
  PRIMARY_BUTTON_ICON: `${CSS_CLASSES.BUTTON.PRIMARY} ${CSS_CLASSES.ICON_CONTAINER.MEDIUM} ${COLORS.PRIMARY.LIGHT} ${COLORS.PRIMARY.MAIN}`,
  
  // Card with primary button
  CARD_WITH_PRIMARY_BUTTON: `${CSS_CLASSES.CARD.DEFAULT} ${CSS_CLASSES.BUTTON.PRIMARY}`,
  
  // Input with focus states
  INPUT_WITH_FOCUS: `${CSS_CLASSES.INPUT.FIELD} focus:ring-2 focus:ring-blue-500 focus:border-transparent`,
  
  // Status indicator
  STATUS_INDICATOR: `${CSS_CLASSES.STATUS_PILL.BASE} ${CSS_CLASSES.STATUS_PILL.ONLINE}`,
};

// ==================== HELPER FUNCTIONS ====================
export const getStatusColor = (status) => {
  switch (status) {
    case STATUS.ONLINE:
      return COLORS.STATUS.ONLINE;
    case STATUS.BUSY:
      return COLORS.STATUS.BUSY;
    case STATUS.AWAY:
      return COLORS.STATUS.AWAY;
    case STATUS.OFFLINE:
      return COLORS.STATUS.OFFLINE;
    default:
      return COLORS.STATUS.ONLINE;
  }
};

export const getStatusPillClass = (status) => {
  switch (status) {
    case STATUS.ONLINE:
      return `${CSS_CLASSES.STATUS_PILL.BASE} ${CSS_CLASSES.STATUS_PILL.ONLINE}`;
    case STATUS.BUSY:
      return `${CSS_CLASSES.STATUS_PILL.BASE} ${CSS_CLASSES.STATUS_PILL.BUSY}`;
    case STATUS.AWAY:
      return `${CSS_CLASSES.STATUS_PILL.BASE} ${CSS_CLASSES.STATUS_PILL.AWAY}`;
    case STATUS.OFFLINE:
      return `${CSS_CLASSES.STATUS_PILL.BASE} ${CSS_CLASSES.STATUS_PILL.OFFLINE}`;
    default:
      return `${CSS_CLASSES.STATUS_PILL.BASE} ${CSS_CLASSES.STATUS_PILL.ONLINE}`;
  }
};

export const getIconContainerClass = (size = 'medium', color = COLORS.PRIMARY.LIGHT, textColor = COLORS.PRIMARY.MAIN) => {
  const sizeClass = CSS_CLASSES.ICON_CONTAINER[size.toUpperCase()] || CSS_CLASSES.ICON_CONTAINER.MEDIUM;
  return `${sizeClass} ${color} ${textColor}`;
}; 